import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Row, Col, Card, Button, Dropdown, OverlayTrigger, Tooltip } from 'react-bootstrap';
import './insurance-policies.scss';
import { withSwal } from 'react-sweetalert2';
// components

import { useTranslation } from 'react-i18next';
import PageTitle from '../../components/PageTitle';
import Table from '../../components/Table';
import Loader from '../../components/Loader';
import LaddaButton, { EXPAND_LEFT } from 'react-ladda-button';
import MoneyColumn from '../../components/MoneyColumn';
import { addCommas } from '../../utils/addCommas';
import { FormInput } from '../../components';
import { APICore } from '../../utils/api/apiCore';
import { camelCaseToText } from '../../utils/camelCaseToText';
import { SignedDocument } from '../../interfaces/SignedDocument';
import GenerateForm from '../../components/generateForm';
import ClientAutocomplete from '../../components/ClientAutocomplete';
import { ClientFile } from '../../interfaces/ClientFile';
import DropDownColumn from '../../components/dropDowncolumn';
import DateColumn from '../../components/DateColumn';
import DatePicker from 'react-datepicker';
import { User } from '../../interfaces/User';
import { Company } from '../../interfaces/Company';
import { DateRangePicker } from 'rsuite';
import { DateRange } from 'rsuite/esm/DateRangePicker/types';
import Dropzone from 'react-dropzone';
import { FileType } from '../../interfaces/FileType';
import TranslatedColumn from '../../components/TranslatedColumn';
import i18n from 'i18next';
import { getCompanyName } from '../../utils/getCompanyName';
import { formatDate } from '../../utils/formatDate';
import useQueryStringSync from '../../hooks/useQueryStringSync';
import globalErrorHandler from '../../utils/globalErrorHandler';
import AutoSavingInput from '../../components/AutoSavingFormInput';
import ClientColumn from '../../components/ClientColumn';
import { InsurancePolicy, InsurancePolicyMainBranch, InsurancePolicyStatuses } from '../../interfaces/InsurancePolicy';
import { getCompanies } from '../../utils/api/companies';
import { Insurance, InsuranceRecommendation } from '../../interfaces/Insurance';
import { editInsurancePolicy, getInsurancePolicies, getOperationPolicies } from '../../utils/api/insurancePolicy';

export interface Item {
    policy: InsurancePolicy;
    signedDocument: SignedDocument;
}

// main component
const ProductTransfers = withSwal((props: any) => {
    const navigate = useNavigate();
    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);

    const [search, setSearch] = useState('');
    const [clientSearch, setClientSearch] = useState(queryParams.get('clientId') || '');
    const { t } = useTranslation('insurancePolicies');
    const { t: t2 } = useTranslation('insurance');
    const [loading, setLoading] = useState<boolean>(false);
    const [list, setList] = useState<Item[]>([]);
    const [total, setTotal] = useState(0);
    const [totalDepositCommission, setTotalDepositCommission] = useState(0);
    const [totalMonthlyCommission, setTotalMonthlyCommission] = useState(0);
    const [totalPremium, setTotalPremium] = useState(0);
    const [totalClients, setTotalClients] = useState(0);
    const [itemsPerPage, setItemsPerPage] = useState(0);
    const [page, setPage] = useState(0);
    const [companies, setCompanies] = useState([]);
    const [isLoadingExport, setIsLoadingExport] = useState(false);
    const [isLoadingImport, setIsLoadingImport] = useState(false);

    // filters and sorting
    const [status, setStatus] = useState<string>(queryParams.get('status') || '');
    const [company, setCompany] = useState(queryParams.get('company') || '');
    const [product, setProduct] = useState(queryParams.get('product') || '');
    const [action, setAction] = useState<string>(queryParams.get('action') || '');
    const [lastChangeStartDate, setLastChangeStartDate] = useState<Date | undefined>(
        queryParams.get('lastChangeStartDate') ? new Date(queryParams.get('lastChangeStartDate') as string) : undefined,
    );
    const [lastChangeEndDate, setLastChangeEndDate] = useState<Date | undefined>(
        queryParams.get('lastChangeEndDate') ? new Date(queryParams.get('lastChangeEndDate') as string) : undefined,
    );
    const [submissionStartDate, setSubmissionStartDate] = useState<Date | undefined>(
        queryParams.get('submissionStartDate') ? new Date(queryParams.get('submissionStartDate') as string) : undefined,
    );
    const [agentSignedStartDate, setAgentSignedStartDate] = useState<Date | undefined>(
        queryParams.get('agentSignedStartDate') ? new Date(queryParams.get('agentSignedStartDate') as string) : undefined,
    );
    const [agentSignedEndDate, setAgentSignedEndDate] = useState<Date | undefined>(
        queryParams.get('agentSignedEndDate') ? new Date(queryParams.get('agentSignedEndDate') as string) : undefined,
    );
    const [submissionEndDate, setSubmissionEndDate] = useState<Date | undefined>(
        queryParams.get('submissionEndDate') ? new Date(queryParams.get('submissionEndDate') as string) : undefined,
    );
    const [closeStartDate, setCloseStartDate] = useState<Date | undefined>(
        queryParams.get('closeStartDate') ? new Date(queryParams.get('closeStartDate') as string) : undefined,
    );
    const [closeEndDate, setCloseEndDate] = useState<Date | undefined>(
        queryParams.get('closeEndDate') ? new Date(queryParams.get('closeEndDate') as string) : undefined,
    );
    const [employmentStatus, setEmploymentStatus] = useState<string>(queryParams.get('employmentStatus') || '');

    const [sortBy, setSortBy] = useState<string>(queryParams.get('sortBy') || '');
    const [asc, setAsc] = useState<boolean>(queryParams.get('asc') === 'true' || false);

    useQueryStringSync(
        useMemo(
            () => ({
                status,
                company,
                product,
                action,
                lastChangeStartDate,
                lastChangeEndDate,
                submissionStartDate,
                submissionEndDate,
                agentSignedStartDate,
                agentSignedEndDate,
                closeStartDate,
                closeEndDate,
                employmentStatus,
                sortBy,
                asc,
            }),
            [
                status,
                company,
                product,
                action,
                lastChangeStartDate,
                lastChangeEndDate,
                submissionStartDate,
                submissionEndDate,
                agentSignedStartDate,
                agentSignedEndDate,
                closeStartDate,
                closeEndDate,
                employmentStatus,
                sortBy,
                asc,
            ],
        ),
    );

    const { user } = new APICore().getLoggedInUser();

    const statusOptions = Object.entries(InsurancePolicyStatuses)
        .slice(3)
        .map((item) => ({
            label: t(camelCaseToText(item[1])),
            value: item[1],
        }));

    useEffect(() => {
        document.title = t('Policies Operations');

        return () => {
            document.title = 'Better Admin';
        };
    }, []);

    const onSent = (file: ClientFile) => {
        const tmpList = [...list];
        tmpList.forEach((item: Item) => {
            if (item.signedDocument?.file?._id === file._id) {
                item.signedDocument.file.sentToClientDate = new Date().toDateString();
            }
        });
        setList(tmpList);
    };

    const SigningLinkColumn = ({ row }: { row: any }) => {
        const item = row.original;
        const { policy } = item;
        return (
            <div data-test-id="signingLinkColumn">
                <GenerateForm signedDocument={item.signedDocument} policy={policy} onSent={onSent} />
            </div>
        );
    };

    const CheckboxColumn = (params: any) => {
        const { row } = params;

        return (
            <FormInput
                type="checkbox"
                name="checkbox"
                checked={row.original.policy.isAgentAppointed}
                disabled={true}
                className="d-flex align-items-center"
            />
        );
    };

    const ToColumn = ({ row }: { row: any }) => {
        const { policy } = row.original;
        return (
            <div data-test-id="toColumn">
                <div>{t(camelCaseToText(policy.branch))}</div>
                <div>{getCompanyName(policy.company.name)}</div>
                <div>{policy.policyNumber}</div>
            </div>
        );
    };

    const savePolicy = async (policy: InsurancePolicy, update: any) => {
        try {
            const result = await editInsurancePolicy(policy._id, update);
            const index = list.findIndex((item) => item.policy._id === policy._id);
            if (index >= 0) {
                list[index].policy = fixDates(result.policy) as InsurancePolicy;
                if (result.submissionDate) {
                    list[index].policy.submissionDate = new Date(result.policy.submissionDate);
                }

                // if (result.closeDate) {
                //     list[index].policy.closeDate = new Date(result.closeDate);
                // }
                setList([...list]);
            }
        } catch (err: any) {
            globalErrorHandler(err);
        }
    };

    enum YesNo {
        Yes = 'true',
        No = 'false',
    }

    // @ts-ignore
    const DateColumnWithUpdate = ({ policy, list, setList }) => {
        const [isUpdating, setIsUpdating] = useState(false);

        const handleUpdateClick = async () => {
            try {
                setIsUpdating(true);
                await editInsurancePolicy(policy._id, {
                    lastChangeDate: true,
                });
                const index = list.findIndex((item: { policy: { _id: any } }) => item.policy._id === policy._id);
                if (index >= 0) {
                    const newList = [...list];
                    newList[index].policy.lastChangeDate = new Date();
                    setList(newList);
                }
                setIsUpdating(false);
            } catch (error) {
                setIsUpdating(false);
                globalErrorHandler(error);
            }
        };

        return (
            <div>
                <div>{formatDate(policy.lastChangeDate, true)}</div>
                <div>
                    <Button onClick={handleUpdateClick} size="sm">
                        {isUpdating ? 'Updating...' : 'Update'}
                    </Button>
                </div>
            </div>
        );
    };

    const ExpandedRow = (props: { row: any }) => {
        const { row } = props;
        const [policy, setPolicy] = useState<InsurancePolicy>(row.original.policy);
        const [insurance, setInsurance] = useState<Insurance | undefined>(policy?.insurance as Insurance);
        const [agent, setAgent] = useState<User | undefined>(insurance?.agent as User);
        const [timeout, setTimeout] = useState<number>();
        const [isLoadingSubmitB2, setIsLoadingSubmitB2] = useState(false);
        // const [policy, setProductTransfer] = useState<ProductTransfer>(product.policy);

        const onDateChange = (date: Date, fieldName: string) => {
            savePolicy({ [fieldName]: date });
        };

        useEffect(() => {
            list[row.index].policy = policy;
            setList(list);
        }, [policy]);

        const savePolicy = async (update: any) => {
            if (!policy) return;

            try {
                setPolicy({ ...policy, ...update });

                const result = await editInsurancePolicy(policy._id, update);

                const tmpList = [...list];
                const index = tmpList.findIndex((item) => item.policy._id === policy._id);
                tmpList[index].policy.status = result.status;

                // setList(tmpList);

                // setProductTransfer(result.policy);
            } catch (err: any) {
                setPolicy(policy);
                globalErrorHandler(err);
            }
        };
        const onTextFieldChanged = (value: string, key: string) => {
            setPolicy({ ...policy, [key]: value });
            if (timeout) {
                window.clearTimeout(timeout);
            }
            const tmp = window.setTimeout(() => {
                savePolicy({ [key]: value });
            }, 5000);
            setTimeout(tmp);
        };

        const InsurancePolicyDropDown = (params: { policy: InsurancePolicy; name: string; options: any; placeholder: string }) => {
            const [pt, setPT] = useState<any>(policy);

            useEffect(() => {
                setPT(policy);
            }, [policy]);

            return (
                <Dropdown className="select-action" align={i18n.dir() === 'ltr' ? 'start' : 'end'}>
                    <Dropdown.Toggle variant="light">
                        {pt[params.name] === undefined
                            ? params.placeholder
                            : pt[params.name] === 'true' || pt[params.name] === true
                              ? t('Yes')
                              : pt[params.name] === 'false' || pt[params.name] === false
                                ? t('No')
                                : t(camelCaseToText(pt[params.name]))}
                        <i className="mdi mdi-chevron-down"></i>
                    </Dropdown.Toggle>
                    <Dropdown.Menu data-test-id="select">
                        {Object.entries(params.options).map(([key, value], i) => (
                            <Dropdown.Item
                                key={key}
                                onClick={() =>
                                    savePolicy({
                                        [params.name]: value === 'true' ? true : value === 'false' ? false : value,
                                    })
                                }
                            >
                                {t(camelCaseToText(key))}
                            </Dropdown.Item>
                        ))}
                    </Dropdown.Menu>
                </Dropdown>
            );
        };

        return (
            <>
                <div className="policies-expanded">
                    <div className="data-div">
                        <Row>
                            {!!policy.appendices?.filter((a) => a.isCancelled).length && (
                                <Col xl={3} xs={3}>
                                    <span className="category-name">{t('cancelled appendices')}</span>
                                    <div className="category-value">
                                        {Array.from(new Set(policy.appendices?.filter((a) => a.isCancelled).map((appendix) => appendix.branch))).map(
                                            (branch) => (
                                                <div>{branch}</div>
                                            ),
                                        )}
                                    </div>
                                </Col>
                            )}
                            <Col xl={3} xs={3}>
                                <span className="category-name">{t('Insured persons')}</span>
                                <div className="category-value">
                                    {Array.from(new Set(policy.insuredList?.map((person) => person.name))).map((name) => (
                                        <div>{name}</div>
                                    ))}
                                </div>
                            </Col>
                            <Col xl={3} xs={3}>
                                <span className="category-name">{t('Submission date')}</span>
                                <DatePicker
                                    name="dueDate"
                                    selected={policy.submissionDate}
                                    onChange={(date: Date) => onDateChange(date, 'submissionDate')}
                                    dateFormat={'dd/MM/yyyy'}
                                    placeholderText={t('select date')}
                                    customInput={<input className="btn btn-light date-input" data-test-id="estimatedTransferDateInput" />}
                                />
                            </Col>
                            <Col xl={3} xs={3}>
                                <span className="category-name">{t('Submission valid until')}</span>
                                <div className="category-value">{policy.submissionValidUntil ? formatDate(policy.submissionValidUntil) : '-'}</div>
                            </Col>
                            <Col xl={3} xs={3}>
                                <span className="category-name">{t('New Policy Number')}</span>
                                <div>
                                    <AutoSavingInput
                                        type="text"
                                        containerClass=""
                                        value={policy.policyNumber || ''}
                                        name="policy.policyNumber"
                                        className={'btn btn-light policy-input'}
                                        onSave={(value) => savePolicy({ policyNumber: value })}
                                    />
                                </div>
                            </Col>

                            <Col xl={3} xs={3}>
                                <span className="category-name">{t('Missing Data First Date')}</span>
                                <DatePicker
                                    name="dueDate"
                                    selected={policy.missingDataFirstDate}
                                    onChange={(date: Date) => onDateChange(date, 'missingDataFirstDate')}
                                    dateFormat={'dd/MM/yyyy'}
                                    placeholderText={t('select date')}
                                    customInput={<input className="btn btn-light date-input" data-test-id="missingDataFirstDateInput" />}
                                />
                            </Col>
                            <Col xl={3} xs={3}>
                                <span className="category-name">{t('Agent')}</span>
                                <div className="category-value">{agent?.fullName}</div>
                            </Col>
                            <Col xl={3} xs={3}>
                                <span className="category-name">{t('Signed on')}</span>
                                <div className="category-value">{policy.agentSignatureDate ? formatDate(policy.agentSignatureDate) : '-'}</div>
                            </Col>
                            {/* <Col xl={3} xs={3}>
                                <span className="category-name">{t('Expiration date')}</span>
                                <div className="category-value">{policy.expirationDate ? formatDate(policy.expirationDate) : '-'}</div>
                            </Col> */}
                            <Col xl={3} xs={3}>
                                <span className="category-name">{t('Close date')}</span>
                                <DatePicker
                                    name="dueDate"
                                    className="date"
                                    selected={policy.cancellationDate}
                                    onChange={(date: Date) => onDateChange(date, 'cancellationDate')}
                                    dateFormat={'dd/MM/yyyy'}
                                    placeholderText={t('select date')}
                                    customInput={<input className="btn btn-light date-input" />}
                                />
                            </Col>
                            {/* <Col xl={3} xs={3}>
                                <span data-test-id="isRejectedDropDown" className="category-name">
                                    {t('Rejected')}{' '}
                                </span>
                                <ProductTransferDropDown policy={policy} name="isRejected" options={YesNo} placeholder="" />
                            </Col> */}

                            <Col xl={3} xs={3}>
                                <span data-test-id="isCollateral" className="category-name">
                                    {t('Collateral')}
                                </span>
                                <InsurancePolicyDropDown policy={policy} name="isCollateral" options={YesNo} placeholder={t('Select')} />
                            </Col>
                            <Col xl={3} xs={3}>
                                <span data-test-id="isRejected" className="category-name">
                                    {t('Rejected')}
                                </span>
                                <InsurancePolicyDropDown policy={policy} name="isRejected" options={YesNo} placeholder={t('Select')} />
                            </Col>
                            {policy.isRejected && (
                                <Col xl={3} xs={3}>
                                    <span className="category-name">{t('rejectionReason')}</span>
                                    <div>
                                        <textarea
                                            className="btn btn-light policy-input"
                                            placeholder={t('enter rejection reason')}
                                            value={policy.rejectionReason}
                                            onChange={(e: any) => onTextFieldChanged(e.target.value, 'rejectionReason')}
                                        />
                                    </div>
                                </Col>
                            )}

                            <Col xl={3} xs={3}>
                                <span className="category-name">{t('Cancellation date')}</span>
                                <DatePicker
                                    name="dueDate"
                                    selected={policy.cancellationDate}
                                    onChange={(date: Date) => onDateChange(date, 'cancellationDate')}
                                    dateFormat={'dd/MM/yyyy'}
                                    placeholderText={t('select date')}
                                    customInput={<input className="btn btn-light date-input" data-test-id="estimatedTransferDateInput" />}
                                />
                            </Col>

                            <Col xl={3} xs={3}>
                                <span data-test-id="collectionIssue.hasIssue" className="category-name">
                                    {t('Has collection issue')}
                                </span>
                                <InsurancePolicyDropDown policy={policy} name="collectionIssue.hasIssue" options={YesNo} placeholder={t('Select')} />
                            </Col>
                            {policy.collectionIssue?.hasIssue && (
                                <>
                                    <Col xl={3} xs={3}>
                                        <span className="category-name">{t('Collection handled on')}</span>
                                        <DatePicker
                                            name="dueDate"
                                            className="date"
                                            selected={policy.collectionIssue.date}
                                            onChange={(date: Date) => onDateChange(date, 'collectionIssue.date')}
                                            dateFormat={'dd/MM/yyyy'}
                                            placeholderText={t('select date')}
                                            customInput={<input className="btn btn-light date-input" />}
                                        />
                                    </Col>
                                    <Col xl={3} xs={3}>
                                        <span className="category-name">{t('Collection issue remarks')}</span>
                                        <div>
                                            <textarea
                                                className="btn btn-light policy-input"
                                                placeholder={t('enter collection issue remarks')}
                                                value={policy.collectionIssue.remarks}
                                                onChange={(e: any) => onTextFieldChanged(e.target.value, 'collectionIssue.remarks')}
                                            />
                                        </div>
                                    </Col>
                                </>
                            )}
                        </Row>
                    </div>

                    <div className="table-wrapper">
                        {api.getLoggedInUser().user.isAdmin && !!policy.commissions && (
                            <table className="commissions">
                                <thead>
                                    <tr>
                                        <th>{t('One Time Commission Type')}</th>
                                        <th>{t('Parameter')}</th>
                                        <th>{t('Amount')}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr>
                                        <td>{t('One Time')}</td>
                                        <td>{Math.round(policy.commissions.depositPercentage * 10000) / 10000}</td>
                                        <td>₪{addCommas(policy.commissions.depositAmount)}</td>
                                    </tr>
                                    <tr>
                                        <td>{t('Monthly')}</td>
                                        <td>{Math.round(policy.commissions.monthlyPercentage * 10000) / 10000}</td>
                                        <td>₪{addCommas(policy.commissions.monthlyAmount)}</td>
                                    </tr>
                                </tbody>
                            </table>
                        )}

                        {!!policy.replacementPolicies?.length && (
                            <table className="replacement-policies">
                                <thead>
                                    <tr>
                                        <th>{t('Cancelled Policy')}</th>
                                        <th>{t('Company')}</th>
                                        <th>{t('Insurance Type')}</th>
                                        <th>{t('Link')}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {policy.replacementPolicies?.map((rp) => (
                                        <tr>
                                            <td>{rp.policyNumber}</td>

                                            <td>{(rp.company as Company).shortName}</td>
                                            <td>{t2(camelCaseToText(rp.branch))}</td>
                                            <td>
                                                <a href={rp.file?.link}>{t('Cancelation form')}</a>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </div>
                </div>
            </>
        );
    };

    const SignatureDateColumn = (params: any) => {
        const policy = params.row.original.policy;

        return <>{formatDate(policy.agentSignatureDate || policy.clientSignatureDate, true, false)}</>;
    };

    const ActionColumn = (params: any) => {
        const policy = params.row.original.policy;
        if (policy.isNewPolicy) {
            return <>{t('New Policy')}</>;
        }

        return <>{t(policy.recommendation)}</>;
    };
    const columns = [
        {
            Header: t('Client'),
            accessor: 'policy.client.fullName',
            sort: true,
            classes: 'table-policy',
            Cell: ClientColumn,
        },
        {
            Header: t('Recommendation'),
            accessor: 'policy.recommendation',
            sort: true,
            classes: 'table-policy',
            Cell: ActionColumn,
        },
        {
            Header: t('agentAppointment'),
            accessor: 'policy.agentAppointment',
            sort: true,
            classes: 'table-policy',
            Cell: CheckboxColumn,
        },
        {
            Header: t('Status'),
            accessor: 'policy.status',
            sort: true,
            classes: 'table-client',
            Cell: DropDownColumn(statusOptions, (status, row) => savePolicy(row.original.policy, { status })),
        },
        {
            Header: t('Last change'),
            accessor: 'policy.lastChangeDate',
            sort: true,
            classes: 'table-client',
            // @ts-ignore
            Cell: ({ row }) => <DateColumnWithUpdate policy={row.original.policy} list={list} setList={setList} />,
        },
        {
            Header: t('Agent signed date'),
            accessor: 'policy.agentSignatureDate',
            sort: true,
            classes: 'table-client',
            Cell: SignatureDateColumn,
        },
        {
            Header: t('Submission Date'),
            accessor: 'policy.submissionDate',
            sort: true,
            classes: 'table-client',
            Cell: DateColumn,
        },
        {
            Header: t('Insurance Type'),
            accessor: 'policy.branch',
            sort: false,
            classes: 'table-policy',
            Cell: ToColumn,
        },
        {
            Header: t('Premium'),
            accessor: 'policy.totalPremium',
            sort: false,
            classes: 'table-policy',
            Cell: MoneyColumn,
        },
        {
            Header: t('Links'),
            accessor: 'policy.signnow',
            sort: false,
            classes: 'table-policy',
            Cell: SigningLinkColumn,
        },
    ];

    const api = new APICore();
    if (!api.canHandleSensitiveData()) {
        columns.splice(columns.length - 1, 1);
    }

    const fixDates = (obj: InsurancePolicy) => {
        if (!obj) return obj;

        const dateFields = [
            'createdAt',
            'issueDate',
            'insuranceStartDate',
            'insuranceEndDate',
            'submissionDate',
            'submissionValidUntil',
            'productionDate',
            'cancellationDate',
            'clientSignatureDate',
            'spouseSignatureDate',
            'agentSignatureDate',
            'agentFilledDate',
            'lastChangeDate',
            'estimatedTransferDate',
            'closeDate',
            'agentSignatureDate',
            'missingDataFirstDate',
        ] as const;

        dateFields.forEach((field) => {
            const value = (obj as any)[field];
            if (value && typeof value === 'string') {
                (obj as any)[field] = new Date(value);
            }
        });

        return obj;
    };

    const handlePolicies = async (page: number) => {
        setPage(page);

        const params = {
            page,
            search,
            companyId: company,
            branch: product,
            action,
            status: status,
            clientId: clientSearch,
            fromSubmissionDate: submissionStartDate?.toISOString(),
            toSubmissionDate: submissionEndDate?.toISOString(),
            fromLastChangeDate: lastChangeStartDate?.toISOString(),
            toLastChangeDate: lastChangeEndDate?.toISOString(),
            fromAgentSignatureDate: agentSignedStartDate?.toISOString(),
            toAgentSignatureDate: agentSignedEndDate?.toISOString(),
            fromCloseDate: closeStartDate?.toISOString(),
            toCloseDate: closeEndDate?.toISOString(),
            employmentStatus,
            sortBy,
            asc,
        };
        if (params.sortBy === 'fullName') {
            params.sortBy = 'client';
        }

        try {
            setLoading(true);
            const { list, total, itemsPerPage, totalDepositCommission, totalMonthlyCommission, totalPremium, totalClients } =
                await getOperationPolicies(params);

            list.forEach((item: any) => fixDates(item.policy));

            setList(list);
            setTotal(total);
            setTotalDepositCommission(totalDepositCommission);
            setTotalMonthlyCommission(totalMonthlyCommission);
            setTotalPremium(totalPremium);
            setTotalClients(totalClients);
            setItemsPerPage(itemsPerPage);

            if (!companies.length) {
                const { companies } = await getCompanies({ type: 'insurance' });
                setCompanies(companies || []);
            }

            if (queryParams.get('clientId') && !clientSearch) {
                setClientSearch(list[0].policy?.client?.fullName || '');
            }

            setLoading(false);
        } catch (err: any) {
            setLoading(false);
            globalErrorHandler(err);
        }
    };

    useEffect(() => {
        // navigate('/product-transfers')
        handlePolicies(0);
    }, [
        clientSearch,
        status,
        company,
        product,
        search,
        action,
        submissionStartDate,
        submissionEndDate,
        lastChangeStartDate,
        lastChangeEndDate,
        closeStartDate,
        closeEndDate,
        agentSignedStartDate,
        agentSignedEndDate,
        employmentStatus,
        sortBy,
        asc,
    ]);

    const handleFreeSearch = async (search: string) => {
        if (search === '') {
            setClientSearch('');
        }
        setStatus('');
        setSearch(search);
    };

    const onStatusFilterSelected = (e: any) => {
        const status = e.target.value;
        if (status) setStatus(status);
        else setStatus('');
    };

    const onProductFilterSelected = (e: any) => {
        const product = e.target.value;
        if (product) setProduct(product);
        else setProduct('');
    };
    const onCompanyFilterSelected = (e: any) => {
        const company = e.target.value;
        if (company) setCompany(company);
        else setCompany('');
    };

    const onActionFilterSelected = (e: any) => {
        const action = e.target.value;
        setAction(action);
    };

    const onAutocompleteSelect = (value: any) => {
        setStatus('');
        setClientSearch(value);
    };

    const onExport = async () => {
        setIsLoadingExport(true);

        // try {
        //     const result = await exportProductTransfers({
        //         search,
        //         companyId: company,
        //         branch: product,
        //         action,
        //         status,
        //         clientId: clientSearch,
        //         fromSubmissionDate: submissionStartDate?.toISOString(),
        //         toSubmissionDate: submissionEndDate?.toISOString(),
        //         fromLastChangeDate: lastChangeStartDate?.toISOString(),
        //         toLastChangeDate: lastChangeEndDate?.toISOString(),
        //         fromAgentSignatureDate: agentSignedStartDate?.toISOString(),
        //         toAgentSignatureDate: agentSignedEndDate?.toISOString(),
        //         fromCloseDate: closeStartDate?.toISOString(),
        //         toCloseDate: closeEndDate?.toISOString(),
        //         employmentStatus,
        //     });
        //     const blob = await result.blob();
        //     const url = window.URL.createObjectURL(new Blob([blob], { type: blob.type }));
        //     const link = document.createElement('a');
        //     link.href = url;

        //     link.setAttribute('download', `product-transfers.xlsx`);
        //     document.body.appendChild(link);
        //     link.click();
        //     link.parentNode?.removeChild(link);
        // } catch (err) {
        //     globalErrorHandler(err);
        // } finally {
        //     setIsLoadingExport(false);
        // }
    };

    const onSortChanged = (sortBy: string) => {
        const ascending = !sortBy.startsWith('-');
        const fieldName = sortBy.split('.').pop();
        if (fieldName) {
            setSortBy(fieldName);
            setAsc(ascending);
        }
        console.log(fieldName);
    };

    const [isUserPickingPolicies, setIsUserPickingPolicies] = useState(false);
    const [selectedPolicies, setSelectedPolicies] = useState<string[]>([]);

    const onSuccessfulMail = (successfulSentStatuses: string[]) => {
        // setIsUserPickingProducts(false);
        // const updatedList = [...list];
        // updatedList.forEach((item) => {
        //     if (successfulSentStatuses.includes(item.policy._id)) {
        //         item.policy.status = InsurancePolicyStatuses.SentToInsuranceCompany;
        //     }
        // });
        // setList(updatedList);
        // setSelectedPolicies([]);
    };

    const onRowClicked = (row: any) => {
        const { policy } = row.original;
        console.log(policy._id, policy);
        if (!isUserPickingPolicies || policy.status !== InsurancePolicyStatuses.SignedByAgent) {
            return;
        }
        const index = selectedPolicies.findIndex((id: string) => id === policy._id);
        if (index >= 0) {
            selectedPolicies.splice(index, 1);
        } else {
            selectedPolicies.push(policy._id);
        }
        setSelectedPolicies([...selectedPolicies]);
    };

    const rowStyle = (row: any) => {
        const { policy } = row.original;
        const syles: any = {};
        if (isUserPickingPolicies && policy.status === InsurancePolicyStatuses.SignedByAgent) {
            syles.cursor = 'pointer';
        }

        if (selectedPolicies.includes(policy?._id)) {
            syles.backgroundColor = '#e6f7ff';
        }
        return syles;
    };

    return (
        <React.Fragment>
            <div className="policies-operations">
                <PageTitle
                    breadCrumbItems={[
                        {
                            label: t('Policies Operations'),
                            path: '/insurance-policies',
                            active: true,
                        },
                    ]}
                    title={t('Policies Operations')}
                />

                <Row className={i18n.dir()}>
                    <Col xl={12} lg={8}>
                        <Card>
                            <Card.Body>
                                {loading && <Loader />}

                                <Row className="mb-2">
                                    <Col md="3">
                                        <div className="form-label">{t('Search')}</div>
                                        <div className="search-bar form-inline">
                                            <ClientAutocomplete
                                                handleFreeSearch={handleFreeSearch}
                                                onAutocompleteSelect={onAutocompleteSelect}
                                                // defaultValue={queryParams.get('clientFullName') || ''}
                                            />
                                        </div>
                                    </Col>
                                    <Col md="auto">
                                        <FormInput
                                            label={t('Status')}
                                            type="select"
                                            name="task-status"
                                            placeholder={t('Status')}
                                            containerClass={'mb-3'}
                                            value={status}
                                            options={[{ label: t('All'), value: '' }].concat(statusOptions)}
                                            onSelected={onStatusFilterSelected}
                                            // onInputChange={onInputChange}
                                        />
                                    </Col>
                                    <Col md="auto">
                                        <div className="form-label">{t('Last change date')}</div>
                                        <DateRangePicker
                                            placement="auto"
                                            format="dd/MM/yyyy"
                                            placeholder={t('Last change')}
                                            defaultValue={
                                                lastChangeStartDate && lastChangeEndDate ? [lastChangeStartDate, lastChangeEndDate] : undefined
                                            }
                                            onOk={(date: DateRange) => {
                                                setLastChangeStartDate(date[0]);
                                                setLastChangeEndDate(date[1]);
                                            }}
                                            onClean={(event: React.MouseEvent) => {
                                                setLastChangeStartDate(undefined);
                                                setLastChangeEndDate(undefined);
                                            }}
                                        />
                                    </Col>
                                    <Col md="auto">
                                        <div className="form-label">{t('Submission date')}</div>
                                        <DateRangePicker
                                            placement="auto"
                                            data-test-id="submission-date-range-picker"
                                            format="dd/MM/yyyy"
                                            placeholder={t('Submission date')}
                                            defaultValue={
                                                submissionStartDate && submissionEndDate ? [submissionStartDate, submissionEndDate] : undefined
                                            }
                                            onOk={(date: DateRange) => {
                                                setSubmissionStartDate(date[0]);
                                                setSubmissionEndDate(date[1]);
                                            }}
                                            onClean={(event: React.MouseEvent) => {
                                                setSubmissionStartDate(undefined);
                                                setSubmissionEndDate(undefined);
                                            }}
                                        />
                                    </Col>
                                    <Col md="auto">
                                        <div className="form-label">{t('Agent signed date')}</div>
                                        <DateRangePicker
                                            placement="auto"
                                            data-test-id="client-signed-date-range-picker"
                                            format="dd/MM/yyyy"
                                            placeholder={t('Agent signed date')}
                                            defaultValue={
                                                agentSignedStartDate && agentSignedEndDate ? [agentSignedStartDate, agentSignedEndDate] : undefined
                                            }
                                            onOk={(date: DateRange) => {
                                                setAgentSignedStartDate(date[0]);
                                                setAgentSignedEndDate(date[1]);
                                            }}
                                            onClean={(event: React.MouseEvent) => {
                                                setAgentSignedStartDate(undefined);
                                                setAgentSignedEndDate(undefined);
                                            }}
                                        />
                                    </Col>

                                    <Col md="auto">
                                        <div className="form-label">{t('Close date')}</div>
                                        <DateRangePicker
                                            placement="auto"
                                            data-test-id="close-date-range-picker"
                                            format="dd/MM/yyyy"
                                            placeholder={t('Close date')}
                                            defaultValue={closeStartDate && closeEndDate ? [closeStartDate, closeEndDate] : undefined}
                                            onOk={(date: DateRange) => {
                                                setCloseStartDate(date[0]);
                                                setCloseEndDate(date[1]);
                                            }}
                                            onClean={(event: React.MouseEvent) => {
                                                setCloseStartDate(undefined);
                                                setCloseEndDate(undefined);
                                            }}
                                        />
                                    </Col>
                                    <Col md="auto">
                                        <FormInput
                                            data-test-id="search-new-insurance-company"
                                            label={t('Insurance company')}
                                            type="select"
                                            name="task-status"
                                            containerClass={'mb-3'}
                                            options={[{ label: t('All'), value: '' }].concat(
                                                companies.map((c: Company) => ({
                                                    label: c.name,
                                                    value: c._id,
                                                })),
                                            )}
                                            onSelected={onCompanyFilterSelected}
                                            // onInputChange={onInputChange}
                                        />
                                    </Col>
                                    <Col md="auto">
                                        <FormInput
                                            data-test-id="search-new-insurance-product"
                                            label={t('Product type')}
                                            type="select"
                                            name="task-status"
                                            containerClass={'mb-3'}
                                            options={[{ label: t('All'), value: '' }].concat(
                                                [InsurancePolicyMainBranch.lifeInsurance, InsurancePolicyMainBranch.healthInsurance].map((item) => ({
                                                    label: t(camelCaseToText(item)),
                                                    value: item,
                                                })),
                                            )}
                                            onSelected={onProductFilterSelected}
                                            // onInputChange={onInputChange}
                                        />
                                    </Col>

                                    <Col md="auto">
                                        <FormInput
                                            data-test-id="search-active-by-deposits"
                                            label={t('Recommendation')}
                                            type="select"
                                            name="task-status"
                                            placeholder={t('All')}
                                            containerClass={'mb-3'}
                                            options={[
                                                { label: t('All'), value: '' },
                                                ...Object.entries(InsuranceRecommendation).map((item) => ({
                                                    label: t(camelCaseToText(item[0])),
                                                    value: item[1],
                                                })),
                                            ]}
                                            onSelected={onActionFilterSelected}
                                        />
                                    </Col>
                                </Row>
                                <Row className="mb-3">
                                    <Col>
                                        <LaddaButton
                                            data-test-id="export-product-transfers"
                                            loading={isLoadingExport}
                                            data-style={EXPAND_LEFT}
                                            className={'btn btn-primary grouped-btn end'}
                                            dir="ltr"
                                            onClick={() => onExport()}
                                        >
                                            {!isLoadingExport ? <i className="mdi mdi-download me-1" /> : null}
                                            {t('Export')}
                                        </LaddaButton>
                                    </Col>
                                </Row>
                                <Row>
                                    <Col>
                                        {t('Total products')}: {total}
                                    </Col>
                                    <Col>
                                        {t('Total clients')}: {totalClients}
                                    </Col>

                                    <Col>
                                        {t('Total premium')}: ₪{addCommas(totalPremium)}
                                    </Col>
                                    {user.isAdmin && (
                                        <Col>
                                            {t('Total one time commission')}: ₪{addCommas(totalDepositCommission)}
                                        </Col>
                                    )}
                                    {user.isAdmin && (
                                        <Col>
                                            {t('Total monthly commission')}: ₪{addCommas(totalMonthlyCommission)}
                                        </Col>
                                    )}
                                </Row>
                                {itemsPerPage ? (
                                    <Table
                                        columns={columns}
                                        paginationProps={{
                                            onPageSelected: handlePolicies,
                                            totalItems: total,
                                            numberOfPages: Math.ceil(total / itemsPerPage),
                                        }}
                                        isExpandable={true}
                                        // onRowClicked={(row) => oneEditProductTransfer(row.original.policy)}
                                        data={list}
                                        onRowClicked={onRowClicked}
                                        rowStyle={rowStyle}
                                        pageSize={itemsPerPage}
                                        isSortable={true}
                                        ExpandedRow={ExpandedRow}
                                        pagination={true}
                                        isSelectable={false}
                                        tableClass="table-nowrap table-policys"
                                        onSortChanged={onSortChanged}
                                        rowEqualityFn={(idOfCurrent: string, otherRow: any) => idOfCurrent === otherRow.policy?._id}
                                    />
                                ) : null}
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>

                {/* add customer modal */}
                {/*<AddProductTransfer show={showAddProductTransfer} onHide={onCloseAddProductTransferModal} onAddProductTransfer={onAddProductTransfer} policy={selectedProductTransfer}/>*/}
            </div>
        </React.Fragment>
    );
});

export default ProductTransfers;
